import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Document, Page } from 'react-pdf';
import { Modal, ModalProps } from 'react-bootstrap';
import { FieldArrayWithId, useFormContext } from 'react-hook-form';
import { LoadingSpinner } from '../common';
import { ControlledModal } from '../common/ControlledModal';
import { ContractSigningForm } from './schemas';
import { validateDocument } from './uploadDocumentsService';
import { ContractSigningErrorIcon, ContractSigningSuccessIcon } from './Icons';
import { useLoadingMessages } from '../../hooks/useLoadingMessages';
import { getFileSize } from '../../utils/files';

function ContractSigningFilePreviewContent({ file }: { file: File }) {
  const [pages, setPages] = useState(0);

  const handleLoadPages = (documentPages: number) => {
    setPages(documentPages);
  };

  return (
    <Document
      file={URL.createObjectURL(file)}
      onLoadSuccess={({ numPages }) => handleLoadPages(numPages)}
    >
      {Array.from(new Array(pages), (el, index) => (
        <Page
          renderTextLayer={false}
          renderAnnotationLayer={false}
          key={`page_${index + 1}`}
          pageNumber={index + 1}
        />
      ))}
    </Document>

  );
}

function ContractSigningFilePreview({ file, show, onHide }: ModalProps & { file: File | null }) {
  if (!file) return null;
  return (
    <ControlledModal
      title={file.name}
      closeButton
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
    >
      <Modal.Body>
        <ContractSigningFilePreviewContent file={file} />
      </Modal.Body>
    </ControlledModal>
  );
}

function DocumentValidationItemStatusIcon({
  isLoading,
  isSuccess,
  isError,
  isRutValid,
}: {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isRutValid: boolean;
}) {
  // When RUT is invalid, show error state since validation cannot proceed
  if (!isRutValid) {
    return <ContractSigningErrorIcon />;
  }
  if (isLoading) {
    return <LoadingSpinner borderWidth={2} color="secondary" size={18} />;
  }
  // We prefer to use JSX components specifically here for icons instead of static image assets
  // to ensure consistent styling and avoid breaking existing component layouts
  // modifying the static image assets would require updating all the places where they are used
  if (isSuccess) {
    return (
      <ContractSigningSuccessIcon />

    );
  }
  if (isError) {
    return (
      <ContractSigningErrorIcon />
    );
  }
  return null;
}

const LOADING_MESSAGES = [
  { message: 'Estamos cargando el documento...', duration: 3000 },
  { message: 'Estamos validando el contenido del documento...', duration: 2000 },
  { message: 'Estamos finalizando la validación del documento...', duration: 2000 },
];

const INVALID_RUT_MESSAGE = 'Ingrese un RUT válido para validar el documento';

function DocumentValidationItemText(
  {
    file, isError, isLoading, message, isRutValid,
  }: { file: File, isError: boolean, message: string, isLoading: boolean, isRutValid: boolean }) {
  const loadingMessage = useLoadingMessages(LOADING_MESSAGES);

  const fileSizeMB = getFileSize({ file });

  const getDisplayMessage = () => {
    if (!isRutValid) return INVALID_RUT_MESSAGE;
    if (isLoading) return loadingMessage;
    return message;
  };

  return (
    <div className="flex-grow-1 w-100 min-w-0 overflow-hidden d-flex flex-column gap-1">
      <span className="d-inline-block fw-semibold text-truncate text-start w-100" title={file.name}>
        {file.name}
      </span>
      <span
        className="text-muted text-start fs-2xs text-muted"
      >
        {fileSizeMB}
        {' '}
        MB
      </span>
      <span
        title={message}
        className={clsx('d-inline-block fs-xs text-start text-wrap fw-normal', {
          'text-danger': isError || !isRutValid,
        })}
      >
        {getDisplayMessage()}
      </span>
    </div>
  );
}

function DocumentValidationItem(
  {
    file, rut, isRutValid, onRemove, index,
  }:
  {
    file: FieldArrayWithId<ContractSigningForm, 'documentsToSign', 'id'>,
    rut: ContractSigningForm['clientRUT'],
    isRutValid: boolean,
    onRemove: () => void, index: number
  }) {
  const { setError, setValue, clearErrors } = useFormContext();
  const [showPreview, setShowPreview] = useState(false);
  const {
    data, isLoading, isSuccess, isError, error,
  } = useQuery({
    queryKey: ['document-validation', file.id, rut],
    enabled: isRutValid,
    queryFn: async () => {
      try {
        const { message, documentTypeId } = await validateDocument(file.raw, rut);
        setValue(`documentsToSign.${index}.document_type_id`, documentTypeId);
        clearErrors(`documentsToSign.${index}`);
        return { message, document_type_id: documentTypeId };
      } catch (err) {
        setError(`documentsToSign.${index}`, {
          type: 'custom',
          message: (err as Error)?.message || 'Error al validar el documento',
        });
        throw err;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,

  });
  return (
    <>
      <div className="btn-group w-100 document-validation-list-button" role="group" aria-label="ayuda">
        <button type="button" className="btn ps-1 py-1 d-flex flex-row align-items-center gap-3 col-11" onClick={() => setShowPreview(true)}>
          <div className="flex-shrink-0">
            <DocumentValidationItemStatusIcon
              isLoading={isLoading}
              isSuccess={isSuccess}
              isError={isError}
              isRutValid={isRutValid}
            />
          </div>
          <DocumentValidationItemText
            file={file.raw}
            isError={isError}
            message={isSuccess ? data?.message : (error as Error)?.message || ''}
            isLoading={isLoading}
            isRutValid={isRutValid}
          />
        </button>
        <button type="button" className="btn py-0 px-1 col-1" onClick={onRemove}>
          <img src="/static/img/icons/close.svg" alt="close" height={22} width={22} />
        </button>
      </div>
      <ContractSigningFilePreview
        file={file.raw}
        show={showPreview}
        onHide={() => setShowPreview(false)}
      />
    </>
  );
}

export function DocumentsValidationList({
  files, rut, isRutValid, onRemove,
}
: {
  files: FieldArrayWithId<ContractSigningForm, 'documentsToSign', 'id'>[],
  rut: ContractSigningForm['clientRUT'],
  isRutValid: boolean,
  onRemove: (index: number) => void
}) {
  return (
    <ul className="d-flex flex-column gap-2 list-unstyled">
      {files.map((file, index) => (
        <li key={file.id}>
          <DocumentValidationItem
            key={file.id}
            file={file}
            rut={rut}
            isRutValid={isRutValid}
            onRemove={() => onRemove(index)}
            index={index}
          />
        </li>
      ))}
    </ul>
  );
}
